import type { AnalyteKind } from "./catalogue.js";
import type { ResultColour, ResultFlag } from "./work.js";

/**
 * Reference-interval selection, mechanical flags and the staff colour code.
 *
 * Flags and colours talk to the chemist, never to the patient (NOM-007 5.1.12, research/03):
 * the report prints the flag letter and the interval; it never prints a colour or a sentence.
 */

/** One interval row as frozen inside the order item's snapshot. */
export interface SnapshotInterval {
  id: string;
  sex: "M" | "F" | null;
  age_min_days: number | null;
  age_max_days: number | null;
  condition: string | null;
  low: number | null;
  high: number | null;
  expected_text: string | null;
  display_text: string | null;
  /** The branch whose own set this row came from; null or absent = a general row (ADR 0020). */
  branch_id?: string | null;
}

/**
 * The rows an order at `branchId` chooses from (ADR 0020): the analyte's rows for that branch when
 * it has any, else its general rows. Another branch's rows never apply. Sex, age and condition then
 * pick within the set (`pickInterval`).
 */
export function intervalsForBranch<T extends { branch_id?: string | null }>(rows: readonly T[], branchId: string | null): T[] {
  const own = branchId === null ? [] : rows.filter((r) => r.branch_id === branchId);
  return own.length > 0 ? own : rows.filter((r) => (r.branch_id ?? null) === null);
}

export interface SnapshotAnalyte {
  id: string;
  code: string;
  name: string;
  kind: AnalyteKind;
  unit: string | null;
  decimals: number | null;
  options: string[] | null;
  formula: string | null;
  section: string | null;
  sort_order: number;
  /** The row applied for this patient (sex, age, condition), or null when none matched. */
  interval: SnapshotInterval | null;
  /** Text printed in the "Referencia" column: the applied row, or every row when none matched. */
  interval_text: string | null;
}

export interface StudySnapshot {
  snapshot_version: 1;
  study: {
    id: string;
    clave: string;
    name: string;
    version: number;
    kind: "analytic" | "service";
    area_id: string | null;
    area_name: string | null;
    area_sort_order: number;
    sample_type_id: string | null;
    sample_type_name: string | null;
    container_id: string | null;
    container_name: string | null;
    container_colour_hex: string | null;
    container_colour_name: string | null;
    /** Effective values at the order's branch: its override (migration 0015) else the study's. */
    method_name: string | null;
    technique_name: string | null;
    equipment_name: string | null;
    /** The branch the method/technique/equipment were resolved for; absent on snapshots taken
     *  before per-branch overrides existed (and on imported history). */
    resolved_branch_id?: string | null;
    indications: string | null;
    delivery_days: number | null;
    /** Weekday table (ADR 0027); absent on snapshots taken before migration 0028. */
    delivery_by_weekday?: number[] | null;
  };
  patient: { sex: "M" | "F"; age_days: number; condition: string | null };
  analytes: SnapshotAnalyte[];
}

/**
 * Pick the interval row for a patient. Specific beats generic: a row matching the order's
 * condition beats one without; then sex-specific beats any-sex; then an age-bounded row beats
 * an open one. Rows that contradict the patient (other sex, age outside) never match.
 */
export function pickInterval<T extends SnapshotInterval>(rows: T[], p: { sex: "M" | "F"; age_days: number; condition: string | null }): T | null {
  const cond = p.condition?.trim().toLowerCase() || null;
  let best: T | null = null;
  let bestScore = -1;
  for (const r of rows) {
    if (r.sex !== null && r.sex !== p.sex) continue;
    if (r.age_min_days !== null && p.age_days < r.age_min_days) continue;
    if (r.age_max_days !== null && p.age_days > r.age_max_days) continue;
    const rc = r.condition?.trim().toLowerCase() || null;
    if (rc !== null && rc !== cond) continue;
    let score = 0;
    if (rc !== null) score += 100;
    if (r.sex !== null) score += 10;
    if (r.age_min_days !== null || r.age_max_days !== null) score += 1;
    if (score > bestScore) {
      best = r;
      bestScore = score;
    }
  }
  return best;
}

export function intervalDisplay(r: SnapshotInterval, unit: string | null): string | null {
  if (r.display_text) return r.display_text;
  const u = unit ? ` ${unit}` : "";
  if (r.low !== null && r.high !== null) return `${fmtNum(r.low)} - ${fmtNum(r.high)}${u}`;
  if (r.high !== null) return `Menor de ${fmtNum(r.high)}${u}`;
  if (r.low !== null) return `Mayor de ${fmtNum(r.low)}${u}`;
  if (r.expected_text) return r.expected_text;
  return null;
}

/**
 * The "Referencia" text when no row applies to the patient: every row's text, one per line, each
 * distinct text once (the rows structured from one laboratory text all print that same text).
 */
export function allIntervalsText(rows: readonly SnapshotInterval[], unit: string | null): string | null {
  const out: string[] = [];
  for (const r of rows) {
    const t = intervalDisplay(r, unit);
    if (t && !out.includes(t)) out.push(t);
  }
  return out.length ? out.join("\n") : null;
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n);
}

/** A typed text that is a plain number ("1.20", "1,20", "-3"), as a number; anything else is null. */
export function plainNumber(text: string | null): number | null {
  if (text === null) return null;
  const t = text.trim();
  return /^[+-]?\d+(?:[.,]\d+)?$/.test(t) ? Number(t.replace(",", ".")) : null;
}

/**
 * Mechanical flag against the applied interval. Null when there is nothing to compare.
 *
 * A text analyte whose applied interval has bounds is judged like a numeric one when the typed
 * value is a plain number (ADR 0020): the previous system typed most quantitative analytes as
 * text (CREATININA, TGO…), and the capture preview has judged them that way since it was written.
 * Any other text ("<0.5", "NO SE OBSERVA") is not compared with the bounds.
 */
export function computeFlag(
  kind: AnalyteKind,
  value: { numeric: number | null; text: string | null },
  interval: SnapshotInterval | null,
): ResultFlag | null {
  if (kind === "subtitle" || kind === "document") return null;
  if (!interval) return null;
  const bounded = interval.low !== null || interval.high !== null;
  const n = kind === "numeric" ? value.numeric : bounded ? plainNumber(value.text) : null;
  if (kind === "numeric" || n !== null) {
    if (n === null) return null;
    if (interval.low !== null && n < interval.low) return "L";
    if (interval.high !== null && n > interval.high) return "H";
    if (!bounded) return null;
    return "N";
  }
  // text / reference
  if (value.text === null || !interval.expected_text) return null;
  return norm(value.text) === norm(interval.expected_text) ? "N" : "A";
}

function norm(s: string): string {
  return s.trim().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/**
 * Staff colour: green normal, yellow (drawn orange-brown) outside the interval, high or low. The
 * catalogue has no per-analyte critical limits, so nothing is ever "red" here: an earlier distance
 * rule ("more than a quarter of the interval width beyond the bound") marked values such as
 * glucose 520 or urea 1 as critical with no clinical basis, and is gone (Very, 2026-11-03).
 * Red returns only when real critical limits exist in the catalogue. Text mismatches are
 * orange too. This is a screen aid only.
 */
export function computeColour(flag: ResultFlag | null, _numeric: number | null, _interval: SnapshotInterval | null): ResultColour {
  if (flag === null) return "none";
  return flag === "N" ? "green" : "yellow";
}

/** Value as it prints: fixed decimals for numerics, the text otherwise. */
export function formatValue(kind: AnalyteKind, numeric: number | null, text: string | null, decimals: number | null): string {
  if (kind === "numeric" && numeric !== null) {
    return decimals === null ? String(numeric) : numeric.toFixed(decimals);
  }
  return text ?? "";
}

/** Analytes that must carry a value before an item counts as captured. */
export function needsValue(kind: AnalyteKind): boolean {
  return kind === "numeric" || kind === "text" || kind === "reference" || kind === "document";
}

// -- Age units -----------------------------------------------------------------------------------

export type AgeBandUnit = "d" | "m" | "a";

/** Mean Gregorian year. A flat 365 drifts four days by 16 years and hands a 15-year-old adult ranges. */
export const DAYS_PER_YEAR = 365.2425;

/**
 * An age in days for a value in `unit` (días, meses, años). A year is the calendar mean, rounded
 * to whole days (15 años = 5479); a month stays 30 days, the convention every stored row and the
 * importer already use. Patient ages are whole elapsed days (`ageDays` in the API), so a band
 * written in years lines up with birthdays to within a day.
 */
export function ageToDays(value: number, unit: AgeBandUnit): number {
  if (unit === "a") return Math.round(value * DAYS_PER_YEAR);
  return Math.round(value * (unit === "m" ? 30 : 1));
}

/** The inverse of `ageToDays`: the value of `days` in `unit`, or null when it is not an exact multiple. */
export function daysToAgeValue(days: number, unit: AgeBandUnit): number | null {
  if (unit === "d") return days;
  if (unit === "m") return days % 30 === 0 ? days / 30 : null;
  const k = Math.round(days / DAYS_PER_YEAR);
  if (k > 0 && ageToDays(k, "a") === days) return k;
  // Rows saved before the calendar year existed are multiples of 365; they still read as years.
  return days % 365 === 0 ? days / 365 : null;
}
